import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  X as CloseIcon,
  CalendarClock,
  GalleryHorizontalEnd,
  Clapperboard,
  Image as ImageIcon,
  Layers,
  Check,
  Sparkles,
} from "lucide-react";
import {
  calendarApi,
  type CalendarPostPlan,
  type CalendarFormat,
  type CalendarPersona,
} from "@/api/calendar";

/**
 * Tela de revisão em lote da Etapa 1: extrai os posts do calendário aprovado,
 * deixa o usuário marcar/desmarcar e ajustar data/agente, e cria N tarefas.
 */

const FORMAT_META: Record<CalendarFormat, { label: string; icon: typeof Layers }> = {
  carrossel: { label: "Carrossel", icon: GalleryHorizontalEnd },
  reels: { label: "Reel", icon: Clapperboard },
  post: { label: "Post", icon: ImageIcon },
  story: { label: "Story", icon: Layers },
};

const PERSONA_LABEL: Record<CalendarPersona, string> = {
  copywriter: "Copywriter",
  social_media: "Social Media",
  market_analyst: "Analista de Mercado",
};

type Row = CalendarPostPlan & { include: boolean };

export function CalendarTaskReviewModal({
  companyId,
  issueId,
  onClose,
}: {
  companyId: string;
  issueId: string;
  onClose: (createdCount?: number) => void;
}) {
  const queryClient = useQueryClient();
  const [rows, setRows] = useState<Row[]>([]);

  const planQuery = useQuery({
    queryKey: ["calendar-plan", issueId],
    queryFn: () => calendarApi.plan(companyId, issueId),
    staleTime: 5 * 60_000,
  });

  useEffect(() => {
    if (planQuery.data?.posts) {
      setRows(planQuery.data.posts.map((p) => ({ ...p, include: true })));
    }
  }, [planQuery.data]);

  const createMutation = useMutation({
    mutationFn: () =>
      calendarApi.createTasks(
        companyId,
        issueId,
        rows.filter((r) => r.include).map(({ include: _i, ...p }) => p),
      ),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ["companies", companyId, "issues"] });
      onClose(res.created);
    },
  });

  const selected = rows.filter((r) => r.include).length;

  const patchRow = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  return (
    <div
      onClick={() => onClose()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(5,8,16,0.45)",
        zIndex: 300,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "5vh 16px",
        overflowY: "auto",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="wf"
        style={{
          width: "100%",
          maxWidth: 760,
          background: "var(--wf-paper)",
          borderRadius: 16,
          border: "1px solid var(--wf-ink-5)",
          boxShadow: "0 12px 40px rgba(0,0,0,0.18)",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "18px 22px",
            borderBottom: "1px solid var(--wf-ink-5)",
          }}
        >
          <div
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: "rgba(255,112,0,0.12)",
              color: "var(--nd-orange)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <CalendarClock size={18} strokeWidth={1.75} />
          </div>
          <div style={{ flex: 1 }}>
            <div className="wf-display" style={{ fontSize: 17, fontWeight: 700 }}>
              Criar tarefas do calendário
            </div>
            <div style={{ fontSize: 12.5, color: "var(--wf-ink-3)" }}>
              Revise os posts, ajuste o que quiser e crie as tarefas datadas de uma vez.
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose()}
            aria-label="Fechar"
            style={{
              width: 30,
              height: 30,
              borderRadius: 8,
              border: "1px solid var(--wf-ink-5)",
              background: "var(--wf-paper)",
              color: "var(--wf-ink-2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <CloseIcon size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ maxHeight: "62vh", overflowY: "auto", padding: "14px 22px" }}>
          {planQuery.isLoading && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 10,
                padding: "40px 0",
                color: "var(--wf-ink-3)",
              }}
            >
              <Sparkles size={22} strokeWidth={1.5} color="var(--nd-orange)" />
              <span style={{ fontSize: 13 }}>Lendo o calendário e montando os posts…</span>
            </div>
          )}
          {planQuery.isError && (
            <div style={{ padding: 20, color: "var(--nd-orange-pun)", fontSize: 13 }}>
              Não consegui ler o calendário: {(planQuery.error as Error).message}
            </div>
          )}
          {!planQuery.isLoading && rows.length === 0 && !planQuery.isError && (
            <div style={{ padding: 24, textAlign: "center", color: "var(--wf-ink-3)", fontSize: 13 }}>
              Não encontrei posts datados neste calendário.
            </div>
          )}

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {rows.map((r, i) => {
              const meta = FORMAT_META[r.format] ?? FORMAT_META.post;
              const FmtIcon = meta.icon;
              return (
                <div
                  key={i}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: 10,
                    border: "1px solid var(--wf-ink-5)",
                    background: r.include ? "var(--wf-paper)" : "var(--wf-bg)",
                    opacity: r.include ? 1 : 0.55,
                  }}
                >
                  <input
                    type="checkbox"
                    checked={r.include}
                    onChange={(e) => patchRow(i, { include: e.target.checked })}
                    style={{ marginTop: 3, width: 16, height: 16, accentColor: "var(--nd-orange)" }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <input
                      value={r.title}
                      onChange={(e) => patchRow(i, { title: e.target.value })}
                      style={{
                        width: "100%",
                        border: "none",
                        background: "transparent",
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: "var(--wf-ink)",
                        padding: 0,
                        outline: "none",
                        marginBottom: 6,
                      }}
                    />
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 4,
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--nd-orange-pun)",
                          background: "rgba(255,112,0,0.10)",
                          padding: "2px 8px",
                          borderRadius: 999,
                        }}
                      >
                        <FmtIcon size={11} strokeWidth={2} />
                        {meta.label}
                      </span>
                      <input
                        type="date"
                        value={r.date}
                        onChange={(e) => patchRow(i, { date: e.target.value })}
                        style={{
                          border: "1px solid var(--wf-ink-5)",
                          borderRadius: 7,
                          padding: "2px 7px",
                          fontSize: 11.5,
                          fontFamily: "var(--wf-font-body)",
                          color: "var(--wf-ink-2)",
                          outline: "none",
                        }}
                      />
                      <select
                        value={r.agentPersona}
                        onChange={(e) => patchRow(i, { agentPersona: e.target.value as CalendarPersona })}
                        style={{
                          border: "1px solid var(--wf-ink-5)",
                          borderRadius: 7,
                          padding: "2px 6px",
                          fontSize: 11.5,
                          fontFamily: "var(--wf-font-body)",
                          color: "var(--wf-ink-2)",
                          background: "var(--wf-paper)",
                          outline: "none",
                        }}
                      >
                        {(Object.keys(PERSONA_LABEL) as CalendarPersona[]).map((p) => (
                          <option key={p} value={p}>
                            {PERSONA_LABEL[p]}
                          </option>
                        ))}
                      </select>
                      <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>
                        {r.channels.join(" · ")}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "14px 22px",
            borderTop: "1px solid var(--wf-ink-5)",
            background: "var(--wf-bg)",
          }}
        >
          <span style={{ fontSize: 13, color: "var(--wf-ink-2)" }}>
            <strong style={{ color: "var(--wf-ink)" }}>{selected}</strong> de {rows.length} selecionadas
          </span>
          {createMutation.isError && (
            <span style={{ fontSize: 12, color: "var(--nd-orange-pun)" }}>
              {(createMutation.error as Error).message}
            </span>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" className="wf-btn wf-btn--ghost" onClick={() => onClose()}>
            Cancelar
          </button>
          <button
            type="button"
            className="wf-btn wf-btn--primary"
            disabled={selected === 0 || createMutation.isPending}
            onClick={() => createMutation.mutate()}
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Check size={14} strokeWidth={2.25} />
            {createMutation.isPending ? "Criando…" : `Criar ${selected} ${selected === 1 ? "tarefa" : "tarefas"}`}
          </button>
        </div>
      </div>
    </div>
  );
}
